import React from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Section } from '../components/ui/Section';
import { Container } from '../components/ui/Container';
import { SEO } from '../components/SEO';
import { Shield, ArrowRight, Briefcase, CheckCircle, Users, Search, Lock, Eye, AlertTriangle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
export function Organization() {
  const {
    t
  } = useLanguage();
  return <Layout data-id="element-3800">
      <SEO title="Organizational Security Protocol | CyberSecurityProtocol" description="A structured cybersecurity protocol to help organizations establish essential security controls and improve operational resilience." keywords="organizational security, cybersecurity protocol, organizational cybersecurity, security controls, operational resilience, business security" url="https://cybersecurityprotocol.org/organization" data-id="element-3801" />

      <Section spacing="lg" data-id="element-3802">
        <Container size="lg" data-id="element-3803">
          {/* Hero - Mobile Optimized */}
          <div className="text-center mb-12 sm:mb-16 px-4" data-id="element-3804">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-500 text-xs font-medium mb-4 sm:mb-6 border border-cyan-500/20" data-id="element-3805">
              <Briefcase className="h-3 w-3 mr-2" data-id="element-3806" />
              {t('org.badge')}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-4 sm:mb-6 tracking-tight" data-id="element-3807">
              {t('org.title')}{' '}
              <span className="text-cyan-500" data-id="element-3808">{t('org.titleHighlight')}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-6 sm:mb-10" data-id="element-3809">
              {t('org.subtitle')}
            </p>

            {/* CTA Buttons - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4" data-id="element-3810">
              <Link to="/organization/govern" className="w-full sm:w-auto" data-id="element-3811">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-bold w-full sm:w-auto" data-id="element-3812">
                  {t('org.cta.start')}
                </Button>
              </Link>
              <Link to="/risk-assessment?type=organization" className="w-full sm:w-auto" data-id="element-3813">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto" data-id="element-3814">
                  {t('org.cta.assessment')}
                </Button>
              </Link>
            </div>
          </div>

          {/* Why This Matters - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16" data-id="element-3815">
            <Card className="p-4 sm:p-6 bg-slate-900/50 border-slate-800" data-id="element-3816">
              <h3 className="font-bold text-slate-200 mb-2 text-sm sm:text-base" data-id="element-3817">
                {t('org.why.title1')}
              </h3>
              <p className="text-slate-400 text-xs sm:text-sm" data-id="element-3818">
                {t('org.why.desc1')}
              </p>
            </Card>
            <Card className="p-4 sm:p-6 bg-slate-900/50 border-slate-800" data-id="element-3819">
              <h3 className="font-bold text-slate-200 mb-2 text-sm sm:text-base" data-id="element-3820">
                {t('org.why.title2')}
              </h3>
              <p className="text-slate-400 text-xs sm:text-sm" data-id="element-3821">
                {t('org.why.desc2')}
              </p>
            </Card>
            <Card className="p-4 sm:p-6 bg-slate-900/50 border-slate-800 sm:col-span-2 md:col-span-1" data-id="element-3822">
              <h3 className="font-bold text-slate-200 mb-2 text-sm sm:text-base" data-id="element-3823">
                {t('org.why.title3')}
              </h3>
              <p className="text-slate-400 text-xs sm:text-sm" data-id="element-3824">
                {t('org.why.desc3')}
              </p>
            </Card>
          </div>

          {/* Core Security Domains - Mobile Optimized */}
          <div className="mb-12 sm:mb-16" data-id="element-3825">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-6 sm:mb-8 text-center px-4" data-id="element-3826">
              {t('org.domains.title')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-3827">
              <DomainCard icon={Users} title={t('org.domain.govern.title')} description={t('org.domain.govern.desc')} link="/organization/govern" data-id="element-3828" />
              <DomainCard icon={Search} title={t('org.domain.identify.title')} description={t('org.domain.identify.desc')} link="/organization/identify" data-id="element-3829" />
              <DomainCard icon={Lock} title={t('org.domain.protect.title')} description={t('org.domain.protect.desc')} link="/organization/protect" data-id="element-3830" />
              <DomainCard icon={Eye} title={t('org.domain.detect.title')} description={t('org.domain.detect.desc')} link="/organization/detect" data-id="element-3831" />
              <DomainCard icon={AlertTriangle} title={t('org.domain.respond.title')} description={t('org.domain.respond.desc')} link="/organization/respond" data-id="element-3832" />
              <DomainCard icon={RefreshCw} title={t('org.domain.recover.title')} description={t('org.domain.recover.desc')} link="/organization/recover" data-id="element-3833" />
            </div>
          </div>

          {/* Implementation Roadmap - Mobile Optimized */}
          <div className="max-w-4xl mx-auto bg-slate-900 rounded-xl p-4 sm:p-6 md:p-8 border border-slate-800" data-id="element-3834">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-100 mb-4 sm:mb-6" data-id="element-3835">
              {t('org.roadmap.title')}
            </h2>
            <p className="text-sm sm:text-base text-slate-400 mb-6 sm:mb-8" data-id="element-3836">
              {t('org.roadmap.subtitle')}
            </p>
            <div className="space-y-4 sm:space-y-6" data-id="element-3837">
              <RoadmapStep number={1} title={t('org.roadmap.phase1.title')} items={[t('org.roadmap.phase1.item1'), t('org.roadmap.phase1.item2'), t('org.roadmap.phase1.item3'), t('org.roadmap.phase1.item4'), t('org.roadmap.phase1.item5')]} data-id="element-3838" />
              <RoadmapStep number={2} title={t('org.roadmap.phase2.title')} items={[t('org.roadmap.phase2.item1'), t('org.roadmap.phase2.item2'), t('org.roadmap.phase2.item3'), t('org.roadmap.phase2.item4'), t('org.roadmap.phase2.item5')]} data-id="element-3839" />
              <RoadmapStep number={3} title={t('org.roadmap.phase3.title')} items={[t('org.roadmap.phase3.item1'), t('org.roadmap.phase3.item2'), t('org.roadmap.phase3.item3'), t('org.roadmap.phase3.item4'), t('org.roadmap.phase3.item5')]} data-id="element-3840" />
            </div>
          </div>
        </Container>
      </Section>
    </Layout>;
}
interface DomainCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  link: string;
}
function DomainCard({
  icon: Icon,
  title,
  description,
  link
}: DomainCardProps) {
  const {
    t
  } = useLanguage();
  return <Link to={link} className="block h-full" data-id="element-3841">
      <Card hoverEffect className="h-full p-4 sm:p-6 border-t-4 border-t-cyan-500/50 hover:border-t-cyan-500 transition-colors flex flex-col" data-id="element-3842">
        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-3 sm:mb-4" data-id="element-3843">
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-500" data-id="element-3844" />
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-slate-100 mb-2" data-id="element-3845">
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4 flex-grow" data-id="element-3846">
          {description}
        </p>
        <div className="flex items-center text-cyan-500 text-xs sm:text-sm font-medium mt-auto" data-id="element-3847">
          {t('org.domain.viewProtocol')}{' '}
          <ArrowRight className="ml-2 h-3 w-3 sm:h-4 sm:w-4" data-id="element-3848" />
        </div>
      </Card>
    </Link>;
}
interface RoadmapStepProps {
  number: number;
  title: string;
  items: string[];
}
function RoadmapStep({
  number,
  title,
  items
}: RoadmapStepProps) {
  return <div className="flex gap-3 sm:gap-4" data-id="element-3849">
      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-cyan-500 text-slate-950 font-bold flex items-center justify-center text-sm sm:text-base" data-id="element-3850">
        {number}
      </div>
      <div className="flex-1 min-w-0" data-id="element-3851">
        <h3 className="text-base sm:text-lg font-bold text-slate-200 mb-2 sm:mb-3" data-id="element-3852">
          {title}
        </h3>
        <ul className="space-y-1.5 sm:space-y-2" data-id="element-3853">
          {items.map((item, idx) => <li key={idx} className="text-slate-400 text-xs sm:text-sm flex items-start" data-id="element-3854">
              <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-3855" />
              <span className="break-words" data-id="element-3856">{item}</span>
            </li>)}
        </ul>
      </div>
    </div>;
}